import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { CharacterStyleCandidate } from '../../../types';
import { useImageUploadZone } from '../../../hooks/useImageUploadZone';
import { getDropHighlightStyle } from '../../../utils/imageFiles';
import { UploadZoneHint } from '../../../components/UploadZoneHint';
import { tosThumbnail } from '../../../utils/tosImage';
import {
  readImageFileDimensions,
  validateCharacterStyleUploadImageDimensions,
} from '../utils/characterStyleUploadImage';
import { validateImageAspectRatio, aspectRatioToastParams } from '../../../utils/imageDimensions';
import { Lightbox } from './Lightbox';

interface CharacterStylePickerModalProps {
  open: boolean;
  title: string;
  candidates: CharacterStyleCandidate[];
  selectedImageHash?: string;
  hasMore: boolean;
  loading: boolean;
  onFetchMore: () => void;
  onAiRank?: () => void;
  /** 不传则隐藏上传入口(如状态制作的造型图搜索弹窗,只允许从候选中选) */
  onUpload?: (file: File) => void | Promise<void>;
  onSelect: (imageHash: string) => void;
  onConfirm: () => void;
  onClose: () => void;
  multiSelect?: boolean;
  maxSelect?: number;
  selectedUrls?: string[];
  onToggleUrl?: (url: string) => void;
  onMultiConfirm?: (urls: string[]) => void;
  tags?: string[];
  onEditTags?: () => void;
  editTagsDisabled?: boolean;
  dependentActionsDisabled?: boolean;
  dependentActionsDisabledReason?: string;
}

export default function CharacterStylePickerModal({
  open,
  title,
  candidates,
  selectedImageHash,
  hasMore,
  loading,
  onFetchMore,
  onAiRank,
  onUpload,
  onSelect,
  onConfirm,
  onClose,
  multiSelect = false,
  maxSelect,
  selectedUrls = [],
  onToggleUrl,
  onMultiConfirm,
  tags = [],
  onEditTags,
  editTagsDisabled = false,
  dependentActionsDisabled = false,
  dependentActionsDisabledReason,
}: CharacterStylePickerModalProps) {
  const { t } = useTranslation('projectDetail');
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(-1);
  const candidateUrls = candidates.map(c => c.imageUrl);
  // 第一批 API 候选;精排只用这一批。
  const firstBatchApiCandidates = candidates.filter(c => c.batchNo === 1 && c.source === 'api');
  // 旧数据:本批只要有一张 casting 图缺 name 就无法精排(后端要求第一批候选全部有 name,
  // 否则 fail-fast),此时直接隐藏「AI排序」按钮。
  const canAiRank =
    firstBatchApiCandidates.length > 0 &&
    firstBatchApiCandidates.every(c => (c.name ?? '').trim() !== '');

  const handleUploadFile = async (file: File) => {
    if (dependentActionsDisabled) return;
    setUploading(true);
    try {
      let dimensions;
      try {
        dimensions = await readImageFileDimensions(file);
      } catch {
        toast.error(t('characterStyle.uploadImageReadFailed'));
        return;
      }
      const dimensionError = validateCharacterStyleUploadImageDimensions(dimensions);
      if (dimensionError) {
        toast.error(t('characterStyle.uploadImageDimensionInvalid', { ...dimensionError }));
        return;
      }
      const aspectError = validateImageAspectRatio(dimensions);
      if (aspectError) {
        toast.error(t('imageAspectRatioInvalid', aspectRatioToastParams(aspectError)));
        return;
      }
      await onUpload?.(file);
    } finally {
      setUploading(false);
    }
  };

  const zone = useImageUploadZone({
    onFiles: (imgs) => handleUploadFile(imgs[0]),
    disabled: uploading || dependentActionsDisabled,
  });

  return (<>
    <Modal
      open={open}
      title={title}
      titleExtra={!(loading && tags.length === 0) ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          {onAiRank && canAiRank && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={onAiRank}
              disabled={loading}
              title={t('characterStyle.aiRankHint')}
            >
              {t('characterStyle.aiRank')}
            </button>
          )}
          <button
            className="btn btn-primary btn-sm"
            onClick={onFetchMore}
            disabled={loading || dependentActionsDisabled}
          >
            {loading ? t('characterStyle.loading') : t('characterStyle.fetchMore')}
          </button>
        </div>
      ) : undefined}
      onClose={onClose}
      width="1280px"
      footer={(
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            {t('characterStyle.close')}
          </button>
          {multiSelect ? (
            <button
              className="btn btn-primary"
              onClick={() => onMultiConfirm?.(selectedUrls)}
              disabled={selectedUrls.length === 0}
            >
              {maxSelect
                ? t('characterStyle.confirmCount', { selected: selectedUrls.length, max: maxSelect })
                : `${t('characterStyle.confirm')} (${selectedUrls.length})`}
            </button>
          ) : (
            <button className="btn btn-primary" onClick={onConfirm} disabled={!selectedImageHash}>
              {t('characterStyle.confirm')}
            </button>
          )}
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {dependentActionsDisabled && dependentActionsDisabledReason && (
          <p data-testid="picker-dependent-actions-disabled" style={{ margin: 0, color: 'var(--danger)', fontSize: '0.82rem' }}>
            {dependentActionsDisabledReason}
          </p>
        )}
        {tags.length > 0 || onEditTags ? (
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
            {tags.map(tag => (
              <span key={tag} style={{ borderRadius: '10px', background: '#ede9fe', color: '#5b21b6', padding: '2px 7px', fontSize: '0.68rem', fontWeight: 500, border: '1px solid #c4b5fd' }}>{tag}</span>
            ))}
            {onEditTags && (
              <button
                type="button"
                onClick={onEditTags}
                disabled={editTagsDisabled}
                title={t('characterStyle.editTags')}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-2)', cursor: editTagsDisabled ? 'not-allowed' : 'pointer', opacity: editTagsDisabled ? 0.4 : 1, fontSize: '0.68rem', padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                {t('characterStyle.editTags')}
              </button>
            )}
            {loading && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-sm)', color: 'var(--text-2)', fontSize: '0.85rem' }}>
                <span className="loading-spinner" />
                {t('characterStyle.tagsGenerating')}
              </span>
            )}
          </div>
        ) : loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', color: 'var(--text-2)', fontSize: '0.85rem' }}>
            <span className="loading-spinner" />
            {t('characterStyle.tagsGenerating')}
          </div>
        ) : null}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(256px, 1fr))', gap: 'var(--space-sm)' }}>
          {onUpload && (<>
            <button
              type="button"
              {...zone.zoneProps}
              onClick={() => !uploading && !dependentActionsDisabled && fileRef.current?.click()}
              disabled={uploading || dependentActionsDisabled}
              style={{
                aspectRatio: '4/3',
                border: '1px dashed var(--border)',
                borderRadius: 'var(--radius)', transition: 'border-color var(--transition-fast)',
                background: 'var(--surface)',
                color: 'var(--text-2)',
                cursor: uploading || dependentActionsDisabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
                opacity: uploading || dependentActionsDisabled ? 0.6 : 1,
                ...getDropHighlightStyle(zone.isOver),
              }}
            >
              {uploading ? (
                <>
                  <span className="loading-spinner" />
                  <span style={{ fontSize: '0.72rem' }}>{t('characterStyle.loading')}</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>+</span>
                  <span style={{ fontSize: '0.78rem' }}>{t('characterStyle.upload')}</span>
                  <UploadZoneHint />
                </>
              )}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              disabled={dependentActionsDisabled}
              style={{ display: 'none' }}
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (file) {
                  await handleUploadFile(file);
                }
                event.target.value = '';
              }}
            />
          </>)}
          {candidates.map((candidate, index) => {
            const selected = multiSelect
              ? selectedUrls.includes(candidate.imageUrl)
              : candidate.imageHash === selectedImageHash;
            const atLimit = multiSelect && maxSelect != null && selectedUrls.length >= maxSelect && !selected;
            const candidateName = candidate.name?.trim() ?? '';
            const hasScore = typeof candidate.score === 'number' && candidate.score > 0;
            return (
              <div
                key={candidate.id || candidate.imageHash}
                style={{
                  aspectRatio: '4/3',
                  overflow: 'hidden',
                  borderRadius: 'var(--radius)',
                  border: selected ? '3px solid var(--primary)' : '1px solid var(--border)',
                  background: 'var(--surface-2)',
                  position: 'relative',
                  transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
                }}
              >
                <img
                  src={tosThumbnail(candidate.imageUrl, 480)}
                  alt={t('characterStyle.candidateAlt', { n: index + 1 })}
                  onClick={() => setPreviewIndex(index)}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', cursor: 'pointer' }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (atLimit || dependentActionsDisabled) return;
                    if (multiSelect) {
                      onToggleUrl?.(candidate.imageUrl);
                    } else {
                      onSelect(candidate.imageHash);
                    }
                  }}
                  disabled={atLimit || dependentActionsDisabled}
                  aria-pressed={selected}
                  title={selected ? t('characterStyle.selected') : atLimit ? t('characterStyle.refsFull') : t('characterStyle.select')}
                  style={{
                    position: 'absolute', top: 4, right: 4,
                    width: 24, height: 24, borderRadius: '50%',
                    background: selected ? 'var(--primary)' : 'rgba(0,0,0,0.45)',
                    color: '#fff',
                    border: selected ? '2px solid #fff' : '2px solid rgba(255,255,255,0.6)',
                    cursor: atLimit || dependentActionsDisabled ? 'not-allowed' : 'pointer',
                    opacity: atLimit || dependentActionsDisabled ? 0.4 : 1,
                    fontSize: '0.7rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    zIndex: 1,
                    padding: 0,
                  }}
                >
                  {selected ? '✓' : '○'}
                </button>
                {(candidateName || hasScore) && (
                  <div
                    data-testid="casting-candidate-meta"
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      padding: '4px 8px',
                      background: 'rgba(17, 24, 39, 0.72)',
                      color: '#fff',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      lineHeight: 1.3,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'baseline',
                      zIndex: 1,
                      pointerEvents: 'none',
                    }}
                  >
                    {candidateName && (
                      <span
                        data-testid="casting-candidate-name"
                        style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {candidateName}
                      </span>
                    )}
                    {hasScore && (
                      <span data-testid="casting-candidate-score" style={{ flexShrink: 0 }}>
                        {candidateName ? ' · ' : ''}{t('characterStyle.matchScore', { score: candidate.score })}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
    {previewIndex >= 0 && ReactDOM.createPortal(
      <Lightbox
        urls={candidateUrls}
        index={previewIndex}
        onClose={() => setPreviewIndex(-1)}
        onChange={setPreviewIndex}
        {...(multiSelect ? {
          selectedUrls,
          onMultiSelect: (url: string) => onToggleUrl?.(url),
          maxSelectable: maxSelect,
        } : {
          selectedImageUrl: selectedImageHash ? candidates.find(c => c.imageHash === selectedImageHash)?.imageUrl : undefined,
          onSelect: dependentActionsDisabled ? undefined : (url: string) => {
              const candidate = candidates.find(c => c.imageUrl === url);
              if (candidate) onSelect(candidate.imageHash);
            },
        })}
      />,
      document.body,
    )}
  </>
  );
}
